import React, { useState, useEffect } from 'react';
import { RouteFeedback, GymFeedbackSettings } from '../../models/climbing/RouteFeedback';
import { RouteFeedbackService } from '../../services/firebase/routeFeedbackService';
import { useAuth } from '../../contexts/AuthContext';
import { GradeSystem, getGradeAsString } from '../../models/climbing/Grade';
import './RouteFeedbackForm.css';

interface RouteFeedbackFormProps {
  routeId: string;
  gymId: string;
  feedbackSettings: GymFeedbackSettings;
  existingFeedback?: RouteFeedback | null;
  onSubmitted?: () => void;
  onCancel?: () => void;
}

export const RouteFeedbackForm: React.FC<RouteFeedbackFormProps> = ({
  routeId,
  gymId,
  feedbackSettings,
  existingFeedback,
  onSubmitted,
  onCancel
}) => {
  const { currentUser } = useAuth();
  const [stars, setStars] = useState<number | undefined>(existingFeedback?.stars);
  const [suggestedGrade, setSuggestedGrade] = useState<number | undefined>(existingFeedback?.suggestedGrade);
  const [comment, setComment] = useState<string>(existingFeedback?.comment || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStarClick = (rating: number) => {
    setStars(rating === stars ? undefined : rating);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to submit feedback');
      return;
    }

    if (!feedbackSettings.allowStars && !feedbackSettings.allowGradeSuggestions && !feedbackSettings.allowComments) {
      setError('No feedback options are enabled for this gym');
      return;
    }

    // Validate that at least one feedback component is provided
    const hasStars = feedbackSettings.allowStars && stars !== undefined;
    const hasGrade = feedbackSettings.allowGradeSuggestions && suggestedGrade !== undefined;
    const hasComment = feedbackSettings.allowComments && comment.trim().length > 0;

    if (!hasStars && !hasGrade && !hasComment) {
      setError('Please provide at least one type of feedback');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const feedbackData: Partial<RouteFeedback> = {
        routeId,
        gymId,
        climberId: currentUser.uid
      };

      if (feedbackSettings.allowStars && stars !== undefined) {
        feedbackData.stars = stars;
      }
      
      if (feedbackSettings.allowGradeSuggestions && suggestedGrade !== undefined) {
        feedbackData.suggestedGrade = suggestedGrade;
      }
      
      if (feedbackSettings.allowComments && comment.trim().length > 0) {
        feedbackData.comment = comment.trim();
      }

      // Use the new createOrUpdateFeedback method which handles both create and update
      await RouteFeedbackService.createOrUpdateFeedback(gymId, feedbackData as Omit<RouteFeedback, 'id' | 'createdAt' | 'updatedAt'>);

      onSubmitted?.();
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Error submitting feedback:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarRating = () => {
    if (!feedbackSettings.allowStars) return null;

    return (
      <div className="feedback-stars">
        <label>Rating:</label>
        <div className="stars-container">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              className={`star ${stars && rating <= stars ? 'active' : ''}`}
              onClick={() => handleStarClick(rating)}
              disabled={isSubmitting}
            >
              ★
            </button>
          ))}
          {stars && (
            <button
              type="button"
              className="clear-stars"
              onClick={() => setStars(undefined)}
              disabled={isSubmitting}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderGradeSuggestion = () => {
    if (!feedbackSettings.allowGradeSuggestions) return null;

    return (
      <div className="feedback-grade">
        <label htmlFor="suggested-grade">Suggested Grade:</label>
        <select
          id="suggested-grade"
          value={suggestedGrade || ''}
          onChange={(e) => setSuggestedGrade(e.target.value ? parseInt(e.target.value) : undefined)}
          disabled={isSubmitting}
        >
          <option value="">Select grade...</option>
          {/* Grade options from 4 to 35 (typical climbing grades) */}
          {Array.from({ length: 32 }, (_, i) => i + 4).map(grade => (
            <option key={grade} value={grade}>
              {getGradeAsString(grade, GradeSystem.FRENCH)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderComment = () => {
    if (!feedbackSettings.allowComments) return null;

    return (
      <div className="feedback-comment">
        <label htmlFor="comment">Comment:</label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your thoughts about this route..."
          maxLength={500}
          disabled={isSubmitting}
        />
        <small>{comment.length}/500</small>
        {feedbackSettings.moderateComments && (
          <small className="moderation-notice">
            Comments are moderated and may take time to appear.
          </small>
        )}
      </div>
    );
  };

  if (!feedbackSettings.enabled) {
    return <div className="feedback-disabled">Route feedback is not enabled for this gym.</div>;
  }

  return (
    <div className="route-feedback-form">
      <h3>{existingFeedback ? 'Update Your Feedback' : 'Add Your Feedback'}</h3>
      
      <form onSubmit={handleSubmit}>
        {renderStarRating()}
        {renderGradeSuggestion()}
        {renderComment()}
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? 'Submitting...' : (existingFeedback ? 'Update Feedback' : 'Submit Feedback')}
          </button>
          
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
